// saturation, lighteness
export const stringToHslColor = (str, s = 30, l = 80) => {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  var h = hash % 360;
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
}

export const stringToHexColor = (str, s = 30, l = 80) => {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  var h = hash % 360;
  const hsl = 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
  return hslToHex(hsl, s, l);
}


function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');   // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Function to convert a hex color to RGB
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// Function to convert a hex color to RGBA
// Hex could be #333 or #CCCF or #333333 or #CACACAFF
// Default Alpha is 1 or FF
export const hexToRgba = (hex) => {
  // Normalize the hex string
  const normalizedHex = hex.replace(/^#/, '');

  // Match different lengths of hex strings (3, 4, 6, 8 characters)
  if (/^[a-f\d]{3}$/i.test(normalizedHex)) {
    // Expand shorthand (e.g., #abc -> #aabbcc)
    const r = normalizedHex[0] + normalizedHex[0];
    const g = normalizedHex[1] + normalizedHex[1];
    const b = normalizedHex[2] + normalizedHex[2];
    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16), a: 1 };
  } else if (/^[a-f\d]{4}$/i.test(normalizedHex)) {
    // Expand shorthand with alpha (e.g., #abcd -> #aabbccdd)
    const r = normalizedHex[0] + normalizedHex[0];
    const g = normalizedHex[1] + normalizedHex[1];
    const b = normalizedHex[2] + normalizedHex[2];
    const a = normalizedHex[3] + normalizedHex[3];
    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16), a: parseInt(a, 16) / 255 };
  } else if (/^[a-f\d]{6}$/i.test(normalizedHex)) {
    // Full hex without alpha
    const r = normalizedHex.slice(0, 2);
    const g = normalizedHex.slice(2, 4);
    const b = normalizedHex.slice(4, 6);
    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16), a: 1 };
  } else if (/^[a-f\d]{8}$/i.test(normalizedHex)) {
    // Full hex with alpha
    const r = normalizedHex.slice(0, 2);
    const g = normalizedHex.slice(2, 4);
    const b = normalizedHex.slice(4, 6);
    const a = normalizedHex.slice(6, 8);
    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16), a: parseInt(a, 16) / 255 };
  }

  // Return null if the input is invalid
  return null;
};

// Function to calculate luminance of an RGBA color
export const luminance = ({ r, g, b, a = 1 }) => {
  // Normalize alpha (optional if input is already between 0-1)
  const alpha = Math.max(0, Math.min(1, a));

  // Calculate luminance with alpha blending (assuming black background)
  const adjustedR = r * alpha;
  const adjustedG = g * alpha;
  const adjustedB = b * alpha;

  return 0.299 * adjustedR + 0.587 * adjustedG + 0.114 * adjustedB;
};

// Function to generate gradient steps with alpha support
export const generateGradient = (color1, color2, steps) => {
  // Convert hex colors to RGBA
  const startColor = hexToRgba(color1);
  const endColor = hexToRgba(color2);

  // Ensure valid alpha values
  const alpha1 = startColor.a ?? 1;
  const alpha2 = endColor.a ?? 1;

  // Calculate the gradient steps
  const step = {
    r: (endColor.r - startColor.r) / (steps - 1),
    g: (endColor.g - startColor.g) / (steps - 1),
    b: (endColor.b - startColor.b) / (steps - 1),
    a: (alpha2 - alpha1) / (steps - 1)
  };

  const gradient = [];

  for (let i = 0; i < steps; i++) {
    const r = Math.round(startColor.r + step.r * i);
    const g = Math.round(startColor.g + step.g * i);
    const b = Math.round(startColor.b + step.b * i);
    const a = (alpha1 + step.a * i).toFixed(2);
    gradient.push(`rgba(${r},${g},${b},${a})`);
  }

  return gradient;
};

// Function to generate gradient steps with alpha support and adjustable rotation
export const generateGradientCircle = (color1, color2, steps, rotate = 0) => {
  // Convert hex colors to RGBA
  const startColor = hexToRgba(color1);
  const endColor = hexToRgba(color2);

  // Ensure valid alpha values
  const alpha1 = startColor.a ?? 1;
  const alpha2 = endColor.a ?? 1;

  // Calculate the gradient steps
  const halfSteps = Math.round((steps - 1) / 2);
  const step = {
    r: (endColor.r - startColor.r) / halfSteps,
    g: (endColor.g - startColor.g) / halfSteps,
    b: (endColor.b - startColor.b) / halfSteps,
    a: (alpha2 - alpha1) / halfSteps
  };

  const gradient = [];

  // Generate first half of gradient
  for (let i = 0; i <= halfSteps; i++) {
    const r = Math.round(startColor.r + step.r * i);
    const g = Math.round(startColor.g + step.g * i);
    const b = Math.round(startColor.b + step.b * i);
    const a = (alpha1 + step.a * i).toFixed(2);
    gradient.push(`rgba(${r},${g},${b},${a})`);
  }

  // Generate second half of gradient in reverse
  for (let i = steps - halfSteps - 1; i >= 0; i--) {
    const r = Math.round(startColor.r + step.r * i);
    const g = Math.round(startColor.g + step.g * i);
    const b = Math.round(startColor.b + step.b * i);
    const a = (alpha1 + step.a * i).toFixed(2);
    gradient.push(`rgba(${r},${g},${b},${a})`);
  }

  // Determine the number of shifts needed to make the first element start from the specified rotation
  const shifts = Math.floor((rotate / 360) * steps);

  // Shift the array to start the gradient from the specified rotation
  for (let j = 0; j < shifts; j++) {
    gradient.push(gradient.shift());
  }

  return gradient;
};

// Example usage:
// const steps = 10;
// const color1 = '#FF5733';
// const color2 = '#33FF57';
// const gradient = generateGradient(color1, color2, steps);

// Example 2
// const color1 = '#FF573300';
// const color2 = '#33FF57FF';

// console.log('Gradient from darker to brighter:', gradient);
